/* Ash and Purl — storefront behaviour (vanilla custom elements, no dependencies). */
(() => {
  const AP = window.AP || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  function formatMoney(cents, format = AP.moneyFormat || '${{amount}}') {
    const value = Number(cents || 0) / 100;
    const fmt = (n, dec, thou, decSep) => {
      const parts = n.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.join(decSep);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        default: return fmt(value, 2, ',', '.');
      }
    });
  }

  function announce(msg) {
    const live = $('#LiveRegion');
    if (live) { live.textContent = ''; setTimeout(() => { live.textContent = msg; }, 50); }
  }

  /* ---------- Overlay + scroll lock ---------- */
  const overlay = {
    el: null,
    owners: new Set(),
    show(owner) {
      this.el = this.el || $('[data-overlay]');
      this.owners.add(owner);
      if (!this.el) return;
      this.el.hidden = false;
      void this.el.offsetWidth;
      this.el.classList.add('is-visible');
      document.body.classList.add('is-locked');
    },
    hide(owner) {
      this.owners.delete(owner);
      if (this.owners.size || !this.el) return;
      this.el.classList.remove('is-visible');
      document.body.classList.remove('is-locked');
      setTimeout(() => { if (!this.owners.size) this.el.hidden = true; }, 240);
    }
  };
  document.addEventListener('click', (e) => {
    if (e.target.matches('[data-overlay]')) document.dispatchEvent(new CustomEvent('cos:close-all'));
  });

  /* ---------- Drawer ---------- */
  class CosDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.firstElementChild;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]')) this.close();
        const toggle = e.target.closest('.menu-drawer__toggle');
        if (toggle) {
          const sub = document.getElementById(toggle.getAttribute('aria-controls'));
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          if (sub) sub.hidden = !open;
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });
      document.addEventListener('cos:close-all', () => this.close());
    }
    trapFocus(e) {
      const f = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', this.panel)
        .filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      document.dispatchEvent(new CustomEvent('cos:close-all', { detail: { except: this } }));
      this.opener = opener || document.activeElement;
      this.hidden = false;
      overlay.show(this);
      void this.offsetWidth; // flush styles so the slide-in transition runs
      this.classList.add('is-open');
      (this.panel || this).focus({ preventScroll: true });
      $$(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      overlay.hide(this);
      $$(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      setTimeout(() => { if (!this.classList.contains('is-open')) this.hidden = true; }, 320);
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('ap-drawer', CosDrawer);

  document.addEventListener('cos:close-all', (e) => {
    $$('ap-drawer.is-open, ap-cart-drawer.is-open').forEach((d) => {
      if (!e.detail || e.detail.except !== d) d.close();
    });
  }, true);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-drawer]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.openDrawer);
    if (drawer && drawer.open) { e.preventDefault(); drawer.open(opener); }
  });

  /* ---------- Header ---------- */
  class CosHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-section') || this;
      const onScroll = () => this.wrapper.classList.toggle('is-scrolled', window.scrollY > 4);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });

      this.search = $('ap-search', this);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-open-search]')) { e.preventDefault(); this.search && this.search.open(); }
      });

      // Dropdowns: keep aria-expanded in step with hover / focus; Escape closes.
      $$('.site-nav__item--parent', this).forEach((item) => {
        const link = item.querySelector(':scope > .site-nav__link');
        const set = (v) => link && link.setAttribute('aria-expanded', String(v));
        item.addEventListener('mouseenter', () => set(true));
        item.addEventListener('mouseleave', () => { set(false); item.classList.remove('is-open'); });
        item.addEventListener('focusin', () => set(true));
        item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) { set(false); item.classList.remove('is-open'); } });
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') { item.classList.remove('is-open'); set(false); link.focus(); link.blur(); }
        });
      });

      // Seal draw-in, first page load of the session only.
      try {
        if (this.dataset.seal !== 'false' && !sessionStorage.getItem('ap-seal') && !reduceMotion) {
          $$('.site-header__logo-full .ap-logo', this).forEach((l) => l.classList.add('ap-logo--animate'));
          sessionStorage.setItem('ap-seal', '1');
        }
      } catch (err) { /* storage blocked */ }
    }
  }
  customElements.define('ap-header', CosHeader);

  /* ---------- Predictive search ---------- */
  class CosSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-results]', this);
      this.timer = null;
      this.controller = null;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.query(this.input.value.trim()), 220);
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close-search]')) this.close(); });
      document.addEventListener('click', (e) => { if (!this.hidden && !this.contains(e.target) && !e.target.closest('[data-open-search]')) this.close(); });
      document.addEventListener('cos:close-all', () => this.close());
    }
    open() {
      this.hidden = false;
      this.input.focus();
      if (this.input.value.trim()) this.query(this.input.value.trim());
    }
    close() {
      if (this.hidden) return;
      this.hidden = true;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
    async query(q) {
      if (!q) { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = `${AP.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,query,page&resources[limit]=6&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const block = doc.querySelector('[data-predictive]');
        this.results.innerHTML = block ? block.outerHTML : '';
        this.results.hidden = !block;
        this.input.setAttribute('aria-expanded', String(!!block));
      } catch (err) {
        if (err.name !== 'AbortError') this.results.hidden = true;
      }
    }
  }
  customElements.define('ap-search', CosSearch);

  /* ---------- Announcement rotation ---------- */
  class CosAnnouncement extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-rotate')) return;
      this.msgs = $$('.announcement__msg', this);
      this.i = 0;
      const go = (d) => {
        this.msgs[this.i].hidden = true;
        this.i = (this.i + d + this.msgs.length) % this.msgs.length;
        this.msgs[this.i].hidden = false;
      };
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-prev]')) go(-1);
        if (e.target.closest('[data-next]')) go(1);
      });
      if (!reduceMotion) setInterval(() => { if (!this.matches(':hover')) go(1); }, Number(this.dataset.interval) || 5000);
    }
  }
  customElements.define('ap-announcement', CosAnnouncement);

  /* ---------- Hero slideshow ---------- */
  class CosSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.hero__slide', this);
      this.dots = $$('[data-dot]', this);
      if (this.slides.length < 2) return;
      this.i = 0;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-prev]')) this.go(this.i - 1);
        if (e.target.closest('[data-next]')) this.go(this.i + 1);
        const dot = e.target.closest('[data-dot]');
        if (dot) this.go(Number(dot.dataset.dot));
      });
      const ms = Number(this.dataset.autoplay);
      if (ms && !reduceMotion) setInterval(() => { if (!this.matches(':hover, :focus-within')) this.go(this.i + 1); }, ms);
    }
    go(n) {
      const len = this.slides.length;
      this.i = (n + len) % len;
      this.slides.forEach((s, k) => {
        s.classList.toggle('is-active', k === this.i);
        s.setAttribute('aria-hidden', String(k !== this.i));
      });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === this.i));
    }
  }
  customElements.define('ap-slideshow', CosSlideshow);

  /* ---------- Product gallery ---------- */
  class CosGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-track]', this);
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb]', this);
      this.dots = $$('.gallery__dot', this);
      this.thumbList = $('[data-thumbs]', this);
      if (!this.track) return;
      const start = this.dataset.start;
      if (start) this.goToMedia(start, false);

      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        if (thumb) this.goToMedia(thumb.dataset.thumb);
        if (e.target.closest('[data-prev]')) this.go(this.index() - 1);
        if (e.target.closest('[data-next]')) this.go(this.index() + 1);
        if (e.target.closest('[data-rail-next]')) this.thumbList.scrollBy({ top: 240 });
        if (e.target.closest('[data-rail-prev]')) this.thumbList.scrollBy({ top: -240 });
        if (e.target.closest('[data-zoom]')) this.zoom();
        else if (e.target.closest('.gallery__slide img') && isDesktop() && !this.closest('ap-modal')) this.zoom();
      });
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.mark(this.index()));
      }, { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index() - 1); }
      });
      const sizeRail = () => {
        const main = $('.gallery__main', this);
        if (main) this.style.setProperty('--gallery-h', `${Math.max(main.offsetHeight - 43, 200)}px`);
        if (this.thumbList) {
          const more = this.thumbList.scrollHeight > this.thumbList.clientHeight + 4;
          $$('.gallery__rail-arrow', this).forEach((a) => { a.style.visibility = more ? 'visible' : 'hidden'; });
        }
      };
      sizeRail();
      window.addEventListener('resize', sizeRail);
      if ('ResizeObserver' in window) new ResizeObserver(sizeRail).observe(this.track);
    }
    index() {
      const w = this.track.clientWidth || 1;
      return Math.round(this.track.scrollLeft / w);
    }
    go(i, smooth = true) {
      const n = this.slides.length;
      if (!n) return;
      const k = (i + n) % n;
      this.track.scrollTo({ left: k * this.track.clientWidth, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      this.mark(k);
    }
    goToMedia(id, smooth = true) {
      const k = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (k > -1) this.go(k, smooth);
    }
    mark(k) {
      this.thumbs.forEach((t, j) => {
        t.classList.toggle('is-active', j === k);
        if (j === k) { t.setAttribute('aria-current', 'true'); } else { t.removeAttribute('aria-current'); }
      });
      this.dots.forEach((d, j) => d.classList.toggle('is-active', j === k));
      const active = this.thumbs[k];
      if (active && this.thumbList) {
        const top = active.offsetTop - this.thumbList.offsetTop;
        if (top < this.thumbList.scrollTop || top + active.offsetHeight > this.thumbList.scrollTop + this.thumbList.clientHeight) {
          this.thumbList.scrollTo({ top: top - 20 });
        }
      }
    }
    zoom() {
      const imgs = this.slides.map((s) => s.querySelector('img')).filter(Boolean);
      if (!imgs.length) return;
      const current = this.slides[this.index()];
      const start = Math.max(0, imgs.indexOf(current && current.querySelector('img')));
      Lightbox.open(imgs, start, (k) => {
        const slide = imgs[k] && imgs[k].closest('.gallery__slide');
        if (slide) this.go(this.slides.indexOf(slide), false);
      });
    }
  }
  customElements.define('ap-gallery', CosGallery);

  /* ---------- Photo viewer: full screen, swipe between photos, tap / pinch to zoom, drag to pan ---------- */
  const Lightbox = {
    build() {
      if (this.el) return;
      const el = document.createElement('div');
      el.className = 'lightbox';
      el.hidden = true;
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Product photos');
      el.innerHTML = `
        <div class="lightbox__bar">
          <span class="lightbox__count" aria-live="polite"></span>
          <button type="button" class="lightbox__close" data-lb-close aria-label="Close photos">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
          </button>
        </div>
        <div class="lightbox__track" tabindex="-1"></div>
        <button type="button" class="lightbox__arrow lightbox__arrow--prev" data-lb-prev aria-label="Previous photo"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg></button>
        <button type="button" class="lightbox__arrow lightbox__arrow--next" data-lb-next aria-label="Next photo"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg></button>
        <p class="lightbox__hint">${window.matchMedia('(hover: hover)').matches ? 'Click to zoom' : 'Tap or pinch to zoom'}</p>`;
      document.body.appendChild(el);
      this.el = el;
      this.track = el.querySelector('.lightbox__track');
      this.count = el.querySelector('.lightbox__count');
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-lb-close]')) this.close();
        else if (e.target.closest('[data-lb-prev]')) this.go(this.index - 1);
        else if (e.target.closest('[data-lb-next]')) this.go(this.index + 1);
      });
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.go(this.index + 1);
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
      });
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const k = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
          if (k !== this.index) { this.reset(); this.mark(k); }
        });
      }, { passive: true });
    },
    open(imgs, start, onClose) {
      this.build();
      this.onClose = onClose;
      this.opener = document.activeElement;
      this.track.innerHTML = '';
      this.slides = imgs.map((img) => {
        const src = img.currentSrc || img.src;
        const big = src.replace(/([?&])width=\d+/, '$1width=2000');
        const slide = document.createElement('div');
        slide.className = 'lightbox__slide';
        slide.innerHTML = `<img class="lightbox__img" src="${big}" alt="${(img.alt || '').replace(/"/g, '&quot;')}" draggable="false">`;
        this.bindZoom(slide);
        this.track.appendChild(slide);
        return slide;
      });
      this.el.classList.toggle('lightbox--single', this.slides.length < 2);
      this.el.hidden = false;
      document.body.classList.add('is-locked');
      requestAnimationFrame(() => this.el.classList.add('is-open'));
      this.track.scrollLeft = start * this.track.clientWidth;
      this.mark(start);
      this.el.querySelector('[data-lb-close]').focus({ preventScroll: true });
    },
    close() {
      if (!this.el || this.el.hidden) return;
      this.reset();
      this.el.classList.remove('is-open');
      this.el.hidden = true;
      this.track.innerHTML = '';
      if (!$('ap-drawer.is-open, ap-cart-drawer.is-open, ap-modal:not([hidden])')) document.body.classList.remove('is-locked');
      if (this.onClose) this.onClose(this.index);
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    },
    go(k) {
      const n = this.slides.length;
      const i = (k + n) % n;
      this.reset();
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.mark(i);
    },
    mark(k) {
      this.index = k;
      this.count.textContent = `${k + 1} / ${this.slides.length}`;
    },
    reset() {
      (this.slides || []).forEach((s) => s.zoom && s.zoom.set(1, 0, 0, true));
      if (this.el) this.el.classList.remove('is-zoomed');
    },
    bindZoom(slide) {
      const img = slide.querySelector('img');
      const MAX = 4;
      const TAP = 2.5;
      const st = { scale: 1, x: 0, y: 0 };
      const pts = new Map();
      let start = null;
      let moved = false;
      const clamp = () => {
        const r = slide.getBoundingClientRect();
        const mx = (r.width * (st.scale - 1)) / 2;
        const my = (r.height * (st.scale - 1)) / 2;
        st.x = Math.min(mx, Math.max(-mx, st.x));
        st.y = Math.min(my, Math.max(-my, st.y));
      };
      const apply = (animate) => {
        img.style.transition = animate && !reduceMotion ? 'transform 220ms ease' : 'none';
        img.style.transform = `translate(${st.x}px, ${st.y}px) scale(${st.scale})`;
        const zoomed = st.scale > 1.01;
        slide.classList.toggle('is-zoomed', zoomed);
        this.el.classList.toggle('is-zoomed', zoomed);
      };
      slide.zoom = {
        set(s, x, y, animate) { st.scale = s; st.x = x; st.y = y; clamp(); apply(animate); },
      };
      const zoomAt = (cx, cy, s) => {
        const r = slide.getBoundingClientRect();
        const ox = cx - (r.left + r.width / 2);
        const oy = cy - (r.top + r.height / 2);
        st.x = -ox * (s - 1);
        st.y = -oy * (s - 1);
        st.scale = s;
        clamp();
        apply(true);
      };
      slide.addEventListener('pointerdown', (e) => {
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        moved = false;
        start = { x: e.clientX, y: e.clientY, sx: st.x, sy: st.y, scale: st.scale, dist: null };
        if (pts.size === 2) {
          const [a, b] = [...pts.values()];
          start.dist = Math.hypot(a.x - b.x, a.y - b.y);
        }
        if (st.scale > 1.01 || pts.size === 2) slide.setPointerCapture(e.pointerId);
      });
      slide.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) {
          // Desktop: a zoomed photo follows the mouse.
          if (e.pointerType === 'mouse' && st.scale > 1.01) {
            const r = slide.getBoundingClientRect();
            st.x = -((e.clientX - r.left) / r.width - 0.5) * r.width * (st.scale - 1);
            st.y = -((e.clientY - r.top) / r.height - 0.5) * r.height * (st.scale - 1);
            clamp(); apply(false);
          }
          return;
        }
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (Math.abs(e.clientX - start.x) + Math.abs(e.clientY - start.y) > 6) moved = true;
        if (pts.size === 2 && start.dist) {
          const [a, b] = [...pts.values()];
          st.scale = Math.min(MAX, Math.max(1, start.scale * (Math.hypot(a.x - b.x, a.y - b.y) / start.dist)));
          clamp(); apply(false);
        } else if (pts.size === 1 && st.scale > 1.01 && e.pointerType !== 'mouse') {
          st.x = start.sx + (e.clientX - start.x);
          st.y = start.sy + (e.clientY - start.y);
          clamp(); apply(false);
        }
      });
      const end = (e) => {
        const wasPinch = pts.size === 2;
        pts.delete(e.pointerId);
        if (e.type === 'pointercancel' || wasPinch) return;
        if (!moved) {
          if (st.scale > 1.01) slide.zoom.set(1, 0, 0, true);
          else zoomAt(e.clientX, e.clientY, TAP);
        }
      };
      slide.addEventListener('pointerup', end);
      slide.addEventListener('pointercancel', end);
    },
  };


  /* ---------- Cart ---------- */
  const Cart = {
    async refresh(html) {
      const drawer = $('#CartDrawer');
      let source = html;
      if (!source) {
        const res = await fetch(`${AP.routes.root}?sections=cart-drawer`);
        const data = await res.json();
        source = data['cart-drawer'];
      }
      const doc = new DOMParser().parseFromString(source, 'text/html');
      const fresh = doc.querySelector('#CartDrawer');
      if (drawer && fresh) {
        const wasOpen = drawer.classList.contains('is-open');
        drawer.firstElementChild.replaceWith(fresh.firstElementChild);
        drawer.panel = drawer.firstElementChild;
        if (wasOpen) drawer.panel.focus({ preventScroll: true });
        drawer.loadUpsell();
      }
    },
    setCount(count) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = !count; });
      $$('.site-header__cart').forEach((a) => a.setAttribute('aria-label', `Bag, ${count} items`));
    },
    async add(items, button) {
      const body = JSON.stringify({ items, sections: 'cart-drawer,cart-json', sections_url: window.location.pathname });
      const res = await fetch(`${AP.routes.cartAdd}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || AP.strings.addError);
      if (data.sections) {
        await this.refresh(data.sections['cart-drawer']);
        const doc = new DOMParser().parseFromString(data.sections['cart-json'], 'text/html');
        try {
          const cart = JSON.parse(doc.querySelector('script').textContent);
          this.setCount(cart.item_count);
        } catch (err) { /* ignore */ }
      }
      if (button) {
        const label = button.querySelector('[data-add-label]') || button;
        const prev = label.textContent;
        label.textContent = AP.strings.added;
        setTimeout(() => { label.textContent = prev; }, 1600);
      }
      announce('Added to your bag');
      if (!document.body.classList.contains('template-cart')) $('#CartDrawer').open(button);
      else window.location.reload();
    },
    async change(key, quantity) {
      const res = await fetch(`${AP.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Cart update failed');
      this.setCount(data.item_count);
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
      await this.refresh(data.sections && data.sections['cart-drawer']);
    }
  };
  AP.cart = Cart;

  class CosCartDrawer extends CosDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
    }
    open(opener) {
      super.open(opener);
      this.loadUpsell();
    }
    async loadUpsell() {
      const box = $('[data-upsell]', this);
      if (!box || box.dataset.loaded) return;
      box.dataset.loaded = '1';
      try {
        const res = await fetch(`${AP.routes.root}recommendations/products?section_id=cart-upsell&product_id=${box.dataset.productId}&limit=6&intent=related`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = doc.querySelector('.cart-upsell__list');
        if (list && list.querySelector('[data-upsell-card]')) {
          box.innerHTML = doc.querySelector('.shopify-section').innerHTML;
          box.hidden = false;
        }
      } catch (err) { /* recommendations unavailable */ }
    }
    async onClick(e) {
      const rm = e.target.closest('[data-remove]');
      const step = e.target.closest('[data-qty-change]');
      const up = e.target.closest('[data-upsell-add]');
      if (rm) { this.busy(rm); await Cart.change(rm.dataset.remove, 0).catch(() => this.unbusy()); }
      if (step) {
        const input = step.parentElement.querySelector('[data-qty-input]');
        const q = Math.max(0, Number(input.value) + Number(step.dataset.qtyChange));
        this.busy(step);
        await Cart.change(step.dataset.key, q).catch(() => this.unbusy());
      }
      if (up) {
        const card = up.closest('[data-upsell-card]');
        const field = card && card.querySelector('[data-upsell-variant]');
        const id = Number(field ? field.value : up.dataset.upsellAdd);
        up.classList.add('is-loading');
        await Cart.add([{ id, quantity: 1 }]).catch(() => up.classList.remove('is-loading'));
      }
      const arrow = e.target.closest('[data-upsell-prev], [data-upsell-next]');
      if (arrow) {
        const track = this.querySelector('[data-upsell-track]');
        if (track) track.scrollBy({ left: (arrow.hasAttribute('data-upsell-next') ? 1 : -1) * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }
    async onChange(e) {
      if (e.target.matches('[data-qty-input]')) {
        this.busy(e.target);
        await Cart.change(e.target.dataset.key, Math.max(0, Number(e.target.value))).catch(() => this.unbusy());
      }
      if (e.target.matches('[data-cart-note]')) saveNote(e.target.value);
    }
    busy(el) { const row = el.closest('.cart-item'); if (row) row.classList.add('is-updating'); }
    unbusy() { $$('.cart-item.is-updating', this).forEach((r) => r.classList.remove('is-updating')); }
  }
  customElements.define('ap-cart-drawer', CosCartDrawer);

  function saveNote(note) {
    fetch(`${AP.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
  }

  // Header bag icon opens the drawer (falls back to /cart without JS).
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-open-cart]');
    if (!link || document.body.classList.contains('template-cart')) return;
    const drawer = $('#CartDrawer');
    if (drawer) { e.preventDefault(); drawer.open(link); }
  });

  // Cart page: steppers, remove and note use the same API.
  const cartPage = $('[data-cart-page]');
  if (cartPage) {
    cartPage.addEventListener('click', async (e) => {
      const rm = e.target.closest('[data-remove]');
      const step = e.target.closest('[data-qty-change]');
      if (rm) await Cart.change(rm.dataset.remove, 0);
      if (step) {
        const input = step.parentElement.querySelector('[data-qty-input]');
        await Cart.change(step.dataset.key, Math.max(0, Number(input.value) + Number(step.dataset.qtyChange)));
      }
    });
    cartPage.addEventListener('change', (e) => {
      if (e.target.matches('[data-qty-input]')) Cart.change(e.target.dataset.key, Math.max(0, Number(e.target.value)));
      if (e.target.matches('[data-cart-note]')) saveNote(e.target.value);
    });
  }

  /* ---------- Product (variant picking + add to bag) ---------- */
  class CosProduct extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.form = document.getElementById(this.dataset.formId);
      this.gallery = $('ap-gallery', this);
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) this.onOptionChange();
        if (e.target.matches('[data-sticky-option]')) this.onStickyChange(e.target);
      });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.setupSticky();
      this.setupShipDate();
    }
    selectedOptions() {
      return $$('fieldset[data-option-index]', this).map((fs) => {
        const checked = fs.querySelector('[data-option-input]:checked');
        return checked ? checked.value : null;
      });
    }
    onOptionChange() {
      const opts = this.selectedOptions();
      $$('fieldset[data-option-index]', this).forEach((fs, i) => {
        const label = fs.querySelector('[data-option-value]');
        if (label) label.textContent = opts[i];
      });
      const variant = this.data.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.update(variant);
      // Size chart rows follow the picked size.
      $$('[data-size-row]').forEach((row) => row.classList.toggle('is-selected', opts.includes(row.dataset.sizeRow)));
    }
    onStickyChange() {
      const opts = $$('[data-sticky-option]', this).map((s) => s.value);
      opts.forEach((val, i) => {
        const input = $$(`fieldset[data-option-index="${i}"] [data-option-input]`, this).find((r) => r.value === val);
        if (input) input.checked = true;
      });
      this.onOptionChange();
    }
    update(variant) {
      const btn = this.form && this.form.querySelector('[data-add-button]');
      const label = btn && btn.querySelector('[data-add-label]');
      const priceEl = btn && btn.querySelector('[data-add-price]');
      const sep = btn && btn.querySelector('[data-add-price-wrap]');
      const stickyBtn = $('[data-sticky-add]', this);
      if (!variant) {
        if (btn) { btn.disabled = true; label.textContent = AP.strings.unavailable; priceEl.hidden = true; sep.hidden = true; }
        if (stickyBtn) { stickyBtn.disabled = true; stickyBtn.textContent = AP.strings.unavailable; }
        return;
      }
      this.variant = variant;
      const idInput = this.form && this.form.querySelector('[data-variant-id]');
      if (idInput) idInput.value = variant.id;
      if (btn) {
        btn.disabled = !variant.available;
        label.textContent = variant.available ? AP.strings.addToBag : 'Sold out';
        priceEl.hidden = !variant.available;
        sep.hidden = !variant.available;
        priceEl.innerHTML = formatMoney(variant.price) + (variant.compare_at_price > variant.price ? ` <s>${formatMoney(variant.compare_at_price)}</s>` : '');
      }
      if (stickyBtn) {
        stickyBtn.disabled = !variant.available;
        stickyBtn.textContent = variant.available ? AP.strings.addToBag : 'Sold out';
      }
      $$('[data-sticky-option]', this).forEach((s, i) => { s.value = variant.options[i]; });
      const wrap = $('[data-price-wrap]', this);
      if (wrap) {
        const onSale = variant.compare_at_price > variant.price;
        wrap.innerHTML = `<div class="price price--lg${onSale ? ' price--sale' : ''}" data-price>`
          + `<span class="visually-hidden">${onSale ? 'Sale price' : 'Price'}</span>`
          + `<span class="price__current">${formatMoney(variant.price)}</span>`
          + (onSale ? `<span class="visually-hidden">Regular price</span><s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : '')
          + (AP.strings.saleLabel ? `<span class="ap-tag ap-tag--sale price__tag">${AP.strings.saleLabel}</span>` : '')
          + '</div>';
      }
      const bnpl = $('[data-bnpl-amount]', this);
      if (bnpl) bnpl.textContent = formatMoney(Math.round(variant.price / 4));
      if (variant.featured_media_id && this.gallery) this.gallery.goToMedia(variant.featured_media_id);
      if (!this.hasAttribute('data-no-history')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    async onSubmit(e) {
      e.preventDefault();
      const btn = this.form.querySelector('[data-add-button]');
      const err = this.form.querySelector('[data-add-error]');
      if (btn.disabled) return;
      err.hidden = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-disabled', 'true');
      try {
        const id = Number(this.form.querySelector('[data-variant-id]').value);
        const modal = this.closest('ap-modal');
        if (modal) modal.close();
        await Cart.add([{ id, quantity: 1 }], btn);
      } catch (ex) {
        err.textContent = ex.message || AP.strings.addError;
        err.hidden = false;
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-disabled');
      }
    }
    setupSticky() {
      const bar = $('[data-sticky-buy]', this);
      const btn = this.form && this.form.querySelector('[data-add-button]');
      if (!bar || !btn) return;
      bar.hidden = false;
      let ticking = false;
      const check = () => {
        ticking = false;
        const past = btn.getBoundingClientRect().bottom < 0;
        bar.classList.toggle('is-visible', past && !isDesktop());
      };
      window.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; setTimeout(check, 50); }
      }, { passive: true });
      window.addEventListener('resize', check);
      check();
      $('[data-sticky-add]', bar).addEventListener('click', () => this.form.requestSubmit());
    }
    setupShipDate() {
      const line = $('[data-ships-in]', this);
      if (!line) return;
      const days = Number(line.dataset.shipsIn);
      if (!days && days !== 0) return;
      const d = new Date();
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) added += 1;
      }
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
      const n = d.getDate();
      const suffix = (n % 10 === 1 && n !== 11) ? 'st' : (n % 10 === 2 && n !== 12) ? 'nd' : (n % 10 === 3 && n !== 13) ? 'rd' : 'th';
      const month = d.toLocaleDateString('en-US', { month: 'long' });
      $('[data-ship-text]', line).textContent = `Ships by: ${n}${suffix} ${month}`;
    }
  }
  customElements.define('ap-product', CosProduct);

  /* ---------- Modal (quick view, zoom) ---------- */
  class CosModal extends HTMLElement {
    connectedCallback() {
      this.content = $('[data-modal-content]', this);
      this.dialog = $('.ap-modal__dialog', this);
      this.addEventListener('click', (e) => {
        if (e.target === this || e.target.closest('[data-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      document.addEventListener('cos:close-all', () => this.close());
    }
    openWith(html, mode) {
      this.opener = document.activeElement;
      this.classList.toggle('ap-modal--zoom', mode === 'zoom');
      this.content.innerHTML = html;
      this.hidden = false;
      document.body.classList.add('is-locked');
      this.dialog.focus();
    }
    close() {
      if (this.hidden) return;
      this.hidden = true;
      this.content.innerHTML = '';
      if (!$('ap-drawer.is-open, ap-cart-drawer.is-open')) document.body.classList.remove('is-locked');
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('ap-modal', CosModal);

  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger || !isDesktop()) return;
    e.preventDefault();
    const modal = $('#QuickView');
    trigger.classList.add('is-loading');
    try {
      const res = await fetch(`${trigger.dataset.quickView}?section_id=quick-view`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const qv = doc.querySelector('.quick-view');
      if (!qv) throw new Error('missing');
      const cart = $('#CartDrawer');
      if (cart && cart.close) cart.close();
      modal.openWith(qv.outerHTML);
    } catch (err) {
      window.location.href = trigger.dataset.quickView;
    } finally {
      trigger.classList.remove('is-loading');
    }
  });

  /* ---------- Product card swatch preview ---------- */
  document.addEventListener('mouseover', (e) => {
    const sw = e.target.closest('[data-swatch-img]');
    if (!sw) return;
    const card = sw.closest('[data-product-card]');
    const img = card && card.querySelector('[data-card-img]');
    if (!img) return;
    if (!img.dataset.origSrc) { img.dataset.origSrc = img.src; img.dataset.origSrcset = img.srcset || ''; }
    img.srcset = '';
    img.src = sw.dataset.swatchImg;
    $$('.card-swatch', card).forEach((s) => s.classList.toggle('is-active', s === sw));
  });

  /* ---------- Recommendations + recently viewed ---------- */
  class CosRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.product-grid__item')) { this.hidden = false; return; }
      // Load after the page settles; the section stays hidden unless it has products.
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const inner = doc.querySelector('ap-recommendations');
          if (inner && inner.querySelector('.product-grid__item')) {
            this.innerHTML = inner.innerHTML;
            this.hidden = false;
            return;
          }
        } catch (err) { /* fall through to the fallback */ }
        const fb = this.querySelector('template[data-fallback]');
        if (fb && fb.content.querySelector('.product-grid__item')) {
          this.innerHTML = '';
          this.appendChild(fb.content.cloneNode(true));
          this.hidden = false;
        }
      };
      if (document.readyState === 'complete') setTimeout(load, 300);
      else window.addEventListener('load', () => setTimeout(load, 300), { once: true });
    }
  }
  customElements.define('ap-recommendations', CosRecommendations);

  class CosRecent extends HTMLElement {
    async connectedCallback() {
      let handles = [];
      try { handles = JSON.parse(localStorage.getItem('ap-recent') || '[]'); } catch (err) { return; }
      handles = handles.filter((h) => h !== this.dataset.exclude).slice(0, Number(this.dataset.limit) || 4);
      if (!handles.length) return;
      const list = $('[data-recent-list]', this);
      const items = await Promise.all(handles.map(async (h) => {
        try {
          const res = await fetch(`${AP.routes.root}products/${h}?section_id=product-card-render`);
          if (!res.ok) return '';
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const li = doc.querySelector('.product-grid__item');
          return li ? li.outerHTML : '';
        } catch (err) { return ''; }
      }));
      const html = items.join('');
      if (html) { list.innerHTML = html; this.hidden = false; }
    }
  }
  customElements.define('ap-recent', CosRecent);

  /* ---------- Card hover photos load on the first mouse hover ---------- */
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const media = e.target.closest && e.target.closest('.card__media--hover');
    if (!media) return;
    const img = media.querySelector('.card__img--secondary[data-src]');
    if (!img) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  });

  /* ---------- Collection sort + filters ---------- */
  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-sort]')) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-filter-form]');
    if (!form) return;
    e.preventDefault();
    const params = new URLSearchParams();
    new FormData(form).forEach((v, k) => { if (v !== '') params.append(k, v); });
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  });
})();
